import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, Search, ChevronDown } from 'lucide-react'
import config from '../utils/config'

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [openDropdown, setOpenDropdown] = useState(null)
  const [dropdownTimeoutId, setDropdownTimeoutId] = useState(null)
  const [mobileExpandedItems, setMobileExpandedItems] = useState({})
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [searchResults, setSearchResults] = useState(null)
  const [searchError, setSearchError] = useState(null)
  const location = useLocation()
  const scrollRafId = useRef(null)
  const mobileMenuRef = useRef(null)
  const searchModalRef = useRef(null)
  const searchToggleRef = useRef(null)
  const mobileMenuToggleRef = useRef(null)
  const prevSearchOpen = useRef(false)
  const prevMobileOpen = useRef(false)

  useEffect(() => {
    const handleScroll = () => {
      if (scrollRafId.current) return
      scrollRafId.current = requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > 10)
        scrollRafId.current = null
      })
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (scrollRafId.current) {
        cancelAnimationFrame(scrollRafId.current)
      }
    }
  }, [])

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [isMobileMenuOpen])

  useEffect(() => {
    document.body.style.overflow = isSearchModalOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [isSearchModalOpen])

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (isSearchModalOpen) {
          setIsSearchModalOpen(false)
        } else if (isMobileMenuOpen) {
          setIsMobileMenuOpen(false)
        }
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isSearchModalOpen, isMobileMenuOpen])

  useEffect(() => {
    if (prevSearchOpen.current && !isSearchModalOpen) {
      searchToggleRef.current?.focus()
    }
    prevSearchOpen.current = isSearchModalOpen
  }, [isSearchModalOpen])

  useEffect(() => {
    if (prevMobileOpen.current && !isMobileMenuOpen) {
      mobileMenuToggleRef.current?.focus()
    }
    prevMobileOpen.current = isMobileMenuOpen
  }, [isMobileMenuOpen])

  const trapFocus = useCallback((containerRef) => {
    const container = containerRef.current
    if (!container) return () => {}
    const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return
      const focusableElements = container.querySelectorAll(focusableSelector)
      if (focusableElements.length === 0) return

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault()
          lastElement.focus()
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault()
          firstElement.focus()
        }
      }
    }

    container.addEventListener('keydown', handleTabKey)
    return () => container.removeEventListener('keydown', handleTabKey)
  }, [])

  useEffect(() => {
    if (isMobileMenuOpen && mobileMenuRef.current) {
      const firstFocusable = mobileMenuRef.current.querySelector('a[href], button:not([disabled])')
      firstFocusable?.focus()
      return trapFocus(mobileMenuRef)
    }
  }, [isMobileMenuOpen, trapFocus])

  useEffect(() => {
    if (isSearchModalOpen && searchModalRef.current) {
      return trapFocus(searchModalRef)
    }
  }, [isSearchModalOpen, trapFocus])

  const handleDropdownMouseEnter = (itemName) => {
    if (dropdownTimeoutId) {
      clearTimeout(dropdownTimeoutId)
      setDropdownTimeoutId(null)
    }
    setOpenDropdown(itemName)
  }

  const handleDropdownMouseLeave = () => {
    const timeoutId = setTimeout(() => {
      setOpenDropdown(null)
    }, 150)
    setDropdownTimeoutId(timeoutId)
  }

  const toggleMobileExpansion = (itemName) => {
    setMobileExpandedItems(prev => ({
      ...prev,
      [itemName]: !prev[itemName]
    }))
  }

  const handleSearchSubmit = async (e) => {
    e.preventDefault()

    if (!searchQuery.trim()) {
      setSearchError('Please enter a search query.')
      return
    }

    setIsSearching(true)
    setSearchResults(null)
    setSearchError(null)

    try {
      const searchData = {
        query: searchQuery.trim(),
        timestamp: new Date().toISOString()
      }

      const response = await fetch(config.searchFormApi, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(searchData)
      })

      if (response.ok) {
        const data = await response.json()

        let parsedSearchData
        if (typeof data.body === 'string') {
          parsedSearchData = JSON.parse(data.body)
        } else if (data.results) {
          parsedSearchData = data
        } else {
          parsedSearchData = data.body
        }

        setSearchResults(parsedSearchData)
      } else {
        const errorText = await response.text()
        throw new Error(`Search failed: ${response.status} ${errorText}`)
      }
    } catch (error) {
      console.error('Search error:', error)
      setSearchResults(null)
      setSearchError('Search failed. Please try again later.')
    } finally {
      setIsSearching(false)
    }
  }

  const getActiveFunction = (itemName) => {
    switch(itemName.toLowerCase()) {
      case 'home': return isHomeActive()
      case 'services': return isServicesActive()
      case 'sectors': return isSectorsActive()
      case 'solutions': return isSolutionsActive()
      case 'partners': return isPartnersActive()
      default: return false
    }
  }

  const navigation = [
    {
      name: 'Home',
      href: '/',
      hasDropdown: true,
      subItems: [
        { name: 'About Us', href: '/about' },
        { name: 'Blog', href: '/blog' },
        { name: 'Careers', href: '/careers' },
        { name: 'News', href: '/news' }
      ]
    },
    {
      name: 'Sectors',
      href: '/sectors',
      hasDropdown: true,
      subItems: [
        { name: 'Finance', href: '/sectors/finance' },
        { name: 'Manufacturing', href: '/sectors/manufacturing' },
        { name: 'Healthcare', href: '/sectors/healthcare' },
        { name: 'High Net Worth', href: '/sectors/high-net-worth' },
        { name: 'Professional Services', href: '/sectors/professional-services' },
        { name: 'Startups', href: '/sectors/startups' }
      ]
    },
    {
      name: 'Services',
      href: '/services',
      hasDropdown: true,
      subItems: [
        { name: 'Application Refactoring', href: '/services/application-refactoring' },
        { name: 'Development Services', href: '/services/development-services' },
        { name: 'Managed Desktops', href: '/services/managed-desktops' },
        { name: 'Managed Help Desk', href: '/services/managed-help-desk' },
        { name: 'Managed Security', href: '/services/managed-security' },
        { name: 'Network Operations', href: '/services/network-operations' },
        { name: 'Open Source Support', href: '/services/open-source-support' },
        { name: 'Project Recovery', href: '/services/project-recovery' },
        { name: 'Vibe Check', href: '/services/vibe-check' },
        { name: 'Get a Quote', href: '/services/quote' }
      ]
    },
    {
      name: 'Solutions',
      href: '/solutions',
      hasDropdown: true,
      subItems: [
        { name: 'Business Process Automation', href: '/solutions/business-process-automation' },
        { name: 'Secure Desktop', href: '/solutions/secure-desktop' },
        { name: 'Email Security', href: '/solutions/email-security' },
        { name: 'Enterprise Security', href: '/solutions/enterprise-security' },
        { name: 'Infrastructure Automation', href: '/solutions/infrastructure-automation' },
        { name: 'Mainframe Migration', href: '/solutions/mainframe-migration' },
        { name: 'Virtual Bench', href: '/solutions/virtual-bench' },
        { name: 'VMware Replatforming', href: '/solutions/vmware-replatforming' }
      ]
    },
    {
      name: 'Partners',
      href: '/partners',
      hasDropdown: true,
      subItems: [
        { name: 'Adobe', href: '/partners/adobe' },
        { name: 'AWS', href: '/partners/aws' },
        { name: 'Cisco', href: '/partners/cisco' },
        { name: 'Dell/EMC', href: '/partners/dell-emc' },
        { name: 'HPE', href: '/partners/hpe' },
        { name: 'IBM', href: '/partners/ibm' },
        { name: 'Microsoft', href: '/partners/microsoft' },
        { name: 'Morpheus', href: '/partners/morpheus' },
        { name: 'N8N', href: '/partners/n8n' },
        { name: 'Nutanix', href: '/partners/nutanix' },
        { name: 'Palo Alto Networks', href: '/partners/palo-alto-networks' },
        { name: 'Workato', href: '/partners/workato' },
        { name: 'View All Partners', href: '/partners' }
      ]
    },
    { name: 'Contact', href: '/contact' },
  ]

  const isActive = (href) => location.pathname === href
  const isHomeActive = () => location.pathname === '/' || location.pathname === '/about' || location.pathname === '/blog' || location.pathname === '/careers' || location.pathname === '/news'
  const isSectorsActive = () => location.pathname.startsWith('/sectors')
  const isServicesActive = () => location.pathname.startsWith('/services')
  const isSolutionsActive = () => location.pathname.startsWith('/solutions')
  const isPartnersActive = () => location.pathname.startsWith('/partners')

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-[100] backdrop-blur-md border-b transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 border-secondary-100 shadow-dc-sm'
          : 'bg-white/[0.88] border-secondary-100'
      }`}
    >
      <div className="container-max">
        <div className="flex items-center justify-between py-4">
          <Link to="/" className="flex items-center space-x-3">
            <img
              src="/images/logo/DCon-Logo_Ball-White_Square-x120.png"
              alt="Disruption Consulting Logo"
              className="w-10 h-10 object-contain"
            />
            <div className="hidden sm:block" >
              <span className="text-xl font-display font-bold text-green-600">Disruption</span>
              <span className="text-xl font-display font-light ml-1 text-green-600">Consulting</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center space-x-8">
            {navigation.map((item) => (
              <div key={item.name} className="relative">
                {item.hasDropdown ? (
                  <div
                    className="relative"
                    onMouseEnter={() => handleDropdownMouseEnter(item.name)}
                    onMouseLeave={handleDropdownMouseLeave}
                  >
                    <Link
                      to={item.href}
                      aria-haspopup="true"
                      aria-expanded={openDropdown === item.name}
                      className={`relative px-3 py-2 text-sm font-display font-medium transition-colors duration-200 flex items-center space-x-1 ${
                        getActiveFunction(item.name)
                          ? 'text-primary-500'
                          : 'text-secondary-700 hover:text-primary-500'
                      }`}
                    >
                      <span>{item.name}</span>
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openDropdown === item.name ? 'rotate-180' : ''}`} />
                    </Link>
                    {getActiveFunction(item.name) && (
                      <motion.div
                        layoutId="activeIndicator"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-700"
                      />
                    )}

                    <AnimatePresence>
                      {openDropdown === item.name && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 mt-1 w-64 bg-white rounded-xl shadow-xl border border-secondary-100 overflow-hidden z-50"
                          onMouseEnter={() => handleDropdownMouseEnter(item.name)}
                          onMouseLeave={handleDropdownMouseLeave}
                        >
                          {item.subItems.map((subItem) => (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              className={`block px-6 py-3 text-sm transition-colors duration-200 ${
                                isActive(subItem.href)
                                  ? 'text-primary-600 bg-primary-50'
                                  : 'text-secondary-700 hover:text-primary-600 hover:bg-secondary-50'
                              }`}
                            >
                              {subItem.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`relative px-3 py-2 text-sm font-display font-medium transition-colors duration-200 ${
                      isActive(item.href)
                        ? 'text-primary-500'
                        : 'text-secondary-700 hover:text-primary-500'
                    }`}
                  >
                    {item.name}
                    {isActive(item.href) && (
                      <motion.div
                        layoutId="activeIndicator"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-700"
                      />
                    )}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          <div className="flex items-center space-x-4">
            <Link
              to="/contact"
              className="hidden lg:inline-flex items-center px-5 py-2 bg-orange-400 hover:bg-orange-700 text-white text-sm font-display font-semibold rounded-full shadow-dc-accent transition-colors duration-200"
            >
              Talk to an Expert
            </Link>
            <button
              ref={searchToggleRef}
              type="button"
              aria-label="Open search"
              onClick={() => setIsSearchModalOpen(true)}
              className="p-2 text-secondary-700 hover:text-primary-500 transition-colors duration-200"
            >
              <Search className="w-5 h-5" />
            </button>

            <button
              ref={mobileMenuToggleRef}
              type="button"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 transition-colors duration-200 ${
                isScrolled
                  ? 'text-secondary-600 hover:text-primary-400'
                  : 'text-secondary-600 hover:text-primary-400'
              }`}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              ref={mobileMenuRef}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="lg:hidden py-4 border-t border-secondary-200 overflow-y-auto max-h-[calc(100vh-5rem)]"
            >
              <nav className="flex flex-col space-y-4">
                {navigation.map((item) => (
                  <div key={item.name}>
                    {item.hasDropdown ? (
                      <div>
                        <div className="flex items-center justify-between">
                          <Link
                            to={item.href}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`block px-3 py-2 text-base font-medium transition-colors duration-200 flex-1 ${
                              getActiveFunction(item.name)
                                ? 'text-primary-400 bg-primary-50 rounded-lg'
                                : 'text-secondary-600 hover:text-primary-400 hover:bg-secondary-50 rounded-lg'
                            }`}
                          >
                            {item.name}
                          </Link>
                          <button
                            type="button"
                            aria-label={`${mobileExpandedItems[item.name] ? 'Collapse' : 'Expand'} ${item.name} submenu`}
                            aria-expanded={!!mobileExpandedItems[item.name]}
                            onClick={() => toggleMobileExpansion(item.name)}
                            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-secondary-600 hover:text-primary-400"
                          >
                            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileExpandedItems[item.name] ? 'rotate-180' : ''}`} />
                          </button>
                        </div>

                        <AnimatePresence>
                          {mobileExpandedItems[item.name] && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="ml-4 mt-2 space-y-2"
                            >
                              {item.subItems.map((subItem) => (
                                <Link
                                  key={subItem.name}
                                  to={subItem.href}
                                  onClick={() => setIsMobileMenuOpen(false)}
                                  className={`block px-3 py-3 text-sm transition-colors duration-200 rounded-lg ${
                                    isActive(subItem.href)
                                      ? 'text-primary-400 bg-primary-50'
                                      : 'text-secondary-600 hover:text-primary-400 hover:bg-secondary-50'
                                  }`}
                                >
                                  {subItem.name}
                                </Link>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <Link
                        to={item.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`block px-3 py-2 text-base font-medium transition-colors duration-200 ${
                          isActive(item.href)
                            ? 'text-primary-400 bg-primary-50 rounded-lg'
                            : 'text-secondary-600 hover:text-primary-400 hover:bg-secondary-50 rounded-lg'
                        }`}
                      >
                        {item.name}
                      </Link>
                    )}
                  </div>
                ))}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {isSearchModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-start justify-center bg-black/50 backdrop-blur-sm pt-20"
            onClick={() => setIsSearchModalOpen(false)}
          >
            <motion.div
              ref={searchModalRef}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-secondary-700">Search</h2>
                  <button
                    type="button"
                    aria-label="Close search"
                    onClick={() => setIsSearchModalOpen(false)}
                    className="p-2 text-secondary-600 hover:text-primary-400 transition-colors duration-200"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleSearchSubmit} className="space-y-4">
                  <div className="relative">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search our website..."
                      className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent outline-none transition-all duration-200"
                      autoFocus
                    />
                    <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-secondary-400" />
                  </div>

                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      aria-label="Clear search"
                      onClick={() => {
                        setSearchQuery('')
                        setSearchResults(null)
                        setSearchError(null)
                      }}
                      className="px-4 py-2 text-secondary-600 hover:text-primary-400 transition-colors duration-200"
                    >
                      Clear
                    </button>

                    <button
                      type="submit"
                      disabled={isSearching || !searchQuery.trim()}
                      className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                        isSearching || !searchQuery.trim()
                          ? 'bg-secondary-300 text-secondary-500 cursor-not-allowed'
                          : 'bg-primary-500 text-white hover:bg-primary-600 shadow-lg hover:shadow-xl'
                      }`}
                    >
                      {isSearching ? 'Searching...' : 'Search'}
                    </button>
                  </div>

                  {searchError && (
                    <div role="alert" className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                      {searchError}
                    </div>
                  )}
                </form>

                {searchResults && searchResults.results && searchResults.results.length > 0 && (
                  <div className="mt-4 border-t border-secondary-200 pt-4">
                    <div className="mb-3">
                      <p className="text-sm text-secondary-600">
                        Found <span className="font-semibold text-secondary-700">{searchResults.total_results}</span> result{searchResults.total_results !== 1 ? 's' : ''} for &quot;<span className="font-semibold text-primary-500">{searchResults.query}</span>&quot;
                      </p>
                    </div>

                    <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                      {searchResults.results.map((result, index) => {
                        const safeUrl = /^(https?:\/\/|\/)/.test(result.url) ? result.url : '#'
                        return (
                          <motion.a
                            key={index}
                            href={safeUrl}
                            className="block p-4 bg-secondary-50 hover:bg-primary-50 rounded-lg transition-all duration-200 hover:shadow-md group"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            onClick={() => {
                              setIsSearchModalOpen(false)
                              setSearchQuery('')
                              setSearchResults(null)
                            }}
                          >
                            <h3 className="text-base font-semibold text-secondary-700 group-hover:text-primary-500 transition-colors duration-200 mb-1">
                              {result.title}
                            </h3>
                            <p className="text-xs text-primary-400 mb-2 font-medium truncate">
                              {safeUrl}
                            </p>
                            <p className="text-sm text-secondary-600 line-clamp-2">
                              {result.snippet}
                            </p>
                            {result.score && (
                              <div className="mt-2 flex items-center">
                                <span className="text-xs text-secondary-500">Relevance: {result.score}</span>
                              </div>
                            )}
                          </motion.a>
                        )
                      })}
                    </div>
                  </div>
                )}

                {searchResults && searchResults.results && searchResults.results.length === 0 && (
                  <div className="mt-4 p-4 bg-secondary-50 border border-secondary-200 rounded-lg text-secondary-600 text-center">
                    <p>No results found for &quot;{searchResults.query}&quot;</p>
                    <p className="text-sm mt-2">Try different keywords or check your spelling.</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  )
}

export default Header
